import React, { useState, useEffect, useCallback, useRef } from 'react';
import API from '../api';
import { useVersion, shortHash } from '../components/VersionCheck';
import UserStatsModal from '../components/UserStatsModal';


const s = {
  page: { padding: '24px 20px', maxWidth: 1440, margin: '0 auto' },
  title: { fontFamily: "'Oswald', sans-serif", fontSize: 28, fontWeight: 700, color: '#fff', marginBottom: 6 },
  subtitle: { color: '#667', fontSize: 14, marginBottom: 28 },

  /*
   * Two columns on a laptop, one on a phone, no media query needed: `auto-fit`
   * fits as many 460px tracks as there is room for, and `min(100%, 460px)` lets
   * that track shrink below 460 on a narrow screen instead of overflowing.
   *
   * `align-items: start` matters — without it every card in a row stretches to
   * the tallest one, so a three-line card next to the pool manager becomes a
   * field of empty space.
   */
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 460px), 1fr))',
    gap: 20,
    alignItems: 'start'
  },
  // For the sections that are really tables — cramming them into half a screen
  // is what made this page hard to use in the first place.
  wide: { gridColumn: '1 / -1' },

  section: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 12,
    padding: '24px',
    margin: 0
  },
  sectionTitle: { fontFamily: "'Oswald', sans-serif", fontSize: 18, fontWeight: 700, color: '#7fb3ff', marginBottom: 6, letterSpacing: 1 },
  sectionDesc: { color: '#667', fontSize: 13, marginBottom: 18 },
  btn: { padding: '11px 22px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: "'Oswald', sans-serif", letterSpacing: 0.5, transition: 'all 0.2s', marginRight: 10, marginBottom: 8 },
  btnPrimary: { background: 'linear-gradient(135deg, #013369, #0a5299)', color: '#fff' },
  btnDanger: { background: 'rgba(213,10,10,0.2)', border: '1px solid rgba(213,10,10,0.4)', color: '#ff8888' },
  btnSuccess: { background: 'rgba(76,175,80,0.2)', border: '1px solid rgba(76,175,80,0.4)', color: '#4caf50' },
  btnSmall: { padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  result: { marginTop: 14, padding: '12px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500 },
  resultSuccess: { background: 'rgba(76,175,80,0.15)', border: '1px solid rgba(76,175,80,0.3)', color: '#4caf50' },
  resultError: { background: 'rgba(213,10,10,0.15)', border: '1px solid rgba(213,10,10,0.3)', color: '#ff8888' },
  input: { padding: '9px 14px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 7, color: '#fff', fontSize: 14, outline: 'none' },
  select: { padding: '9px 12px', background: '#0d1526', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 7, color: '#fff', fontSize: 14, outline: 'none', cursor: 'pointer' },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#889', marginBottom: 5, letterSpacing: 0.5 },
  gameRow: { display: 'flex', alignItems: 'flex-end', gap: 10, marginBottom: 10, flexWrap: 'wrap', padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)' },
  gameField: { display: 'flex', flexDirection: 'column', gap: 4 },
  tbToggle: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700, border: '1px solid rgba(127,179,255,0.3)', background: 'rgba(127,179,255,0.08)', color: '#7fb3ff' },
  removeBtn: { padding: '8px 12px', borderRadius: 6, background: 'rgba(213,10,10,0.15)', border: '1px solid rgba(213,10,10,0.3)', color: '#ff8888', cursor: 'pointer', fontSize: 13, fontWeight: 700 },
  weekItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', gap: 10 },
  weekName: { fontSize: 14, fontWeight: 600, color: '#dde' },
  weekMeta: { fontSize: 12, color: '#556', marginTop: 2 },
  activeBadge: { padding: '3px 10px', borderRadius: 12, background: 'rgba(76,175,80,0.2)', border: '1px solid rgba(76,175,80,0.3)', color: '#4caf50', fontSize: 11, fontWeight: 700 },
  scoreInput: { width: 55, padding: '6px 8px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, color: '#fff', fontSize: 14, fontWeight: 700, textAlign: 'center', outline: 'none' }
};

export default function AdminPage({ user }) {
  const version = useVersion();
  const [counts, setCounts] = useState(null);
  const [recapWeek, setRecapWeek] = useState('');
  const [recapBusy, setRecapBusy] = useState(false);
  const [recapMsg, setRecapMsg] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [funnelSubs, setFunnelSubs] = useState(null);
  const [pushStatus, setPushStatus] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [weekInfo, setWeekInfo] = useState(null);
  const [games, setGames] = useState([]);
  const [week, setWeek] = useState(null);
  const [allWeeks, setAllWeeks] = useState([]);
  const [lockStatus, setLockStatus] = useState(false);
  const [scoreInputs, setScoreInputs] = useState({});
  const [setWinnerResult, setSetWinnerResult] = useState({});
  const [setCurrentResult, setSetCurrentResult] = useState(null);
  // Pool management
  const [allPools, setAllPools] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [poolMembersCache, setPoolMembersCache] = useState({});
  const [newPoolName, setNewPoolName] = useState('');
  const [poolMsg, setPoolMsg] = useState(null);
  const [assignSelect, setAssignSelect] = useState({}); // { poolId: userId }
  const [expandedAdminPool, setExpandedAdminPool] = useState(null);
  const [renameInputs, setRenameInputs] = useState({});
  const [codeInputs, setCodeInputs] = useState({});
  // User management
  const [manageUsers, setManageUsers] = useState([]);
  // The account list is the longest thing on this page by far, so it scrolls in
  // its own pane. A clipped list with no hint reads as the whole list, so this
  // measures whether it actually overflows rather than guessing from a row count.
  const userListRef = useRef(null);
  const [userListScrolls, setUserListScrolls] = useState(false);
  // Which temp-password panels have already been scrolled to. Without this the
  // ref callback would fight the scrollbar on every render.
  const scrolledToTemp = useRef(new Set());
  const [userMsg, setUserMsg] = useState(null);
  // tempPasswords keyed by user id: shows the generated pw inline until dismissed
  const [tempPasswords, setTempPasswords] = useState({});
  // Track the currently-logged-in admin so we can disable self-destructive actions
  const [selfId, setSelfId] = useState(null);
  /* Who the owner is writing to. The chat lives in UserStatsModal, which is the
     same component the profile card opens, so a conversation started here is the
     same thread they see everywhere else rather than a second inbox. */
  const [dmUserId, setDmUserId] = useState(null);

  // Broadcasting into pool chats
  const [castText, setCastText] = useState('');
  const [castPool, setCastPool] = useState('all');
  const [castNotify, setCastNotify] = useState(false);
  const [castBusy, setCastBusy] = useState(false);
  const [castMsg, setCastMsg] = useState(null);
  const [sentLog, setSentLog] = useState([]);

  // DM moderation
  const [support, setSupport] = useState({ requests: [], open: 0, contact: null });
  const [supportLoaded, setSupportLoaded] = useState(false);
  const [supportAll, setSupportAll] = useState(false);

  const [dmThreads, setDmThreads] = useState([]);
  const [dmSelected, setDmSelected] = useState(null); // { userA: {id,username}, userB: {id,username} }
  const [dmThreadMessages, setDmThreadMessages] = useState([]);
  const [dmThreadLoading, setDmThreadLoading] = useState(false);
  const [dmThreadsLoaded, setDmThreadsLoaded] = useState(false);
  // Memberships / paywall
  const [mem, setMem] = useState(null);
  const [memBusy, setMemBusy] = useState(false);
  // Invites
  const [invitePoolId, setInvitePoolId] = useState('');
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviteMsg, setInviteMsg] = useState(null);
  const [inviteSending, setInviteSending] = useState(false);

  const loadData = async () => {
    try {
      const [gamesRes, weeksRes] = await Promise.all([
        API.get('/games'),
        API.get('/games/weeks')
      ]);
      setGames(gamesRes.data.games || []);
      setWeek(gamesRes.data.week);
      if (gamesRes.data.week) setLockStatus(gamesRes.data.week.picks_locked === 1);
      setAllWeeks(weeksRes.data.weeks || []);
    } catch (e) {}
  };

  const loadPoolAdminData = async () => {
    try {
      const [poolsRes, usersRes] = await Promise.all([
        API.get('/pools/all'),
        API.get('/auth/users')
      ]);
      setAllPools(poolsRes.data.pools || []);
      setAllUsers(usersRes.data || []);
      loadSentLog();
    } catch (e) {}
  };

  const loadManageUsers = async () => {
    try {
      const [listRes, meRes] = await Promise.all([
        API.get('/auth/admin/users'),
        API.get('/auth/me')
      ]);
      setManageUsers(listRes.data.users || []);
      setSelfId(meRes.data?.id || null);
    } catch (e) {}
  };

  const flashUserMsg = (ok, text) => {
    setUserMsg({ ok, text });
    setTimeout(() => setUserMsg(null), 5000);
  };

  const resetUserPassword = async (u) => {
    if (!window.confirm(`Generate a new temporary password for ${u.username}? Their current password stops working immediately, and the new one will only be shown once.`)) return;
    try {
      const res = await API.post(`/auth/admin/users/${u.id}/reset-password`);
      setTempPasswords(prev => ({ ...prev, [u.id]: res.data.tempPassword }));
      flashUserMsg(true, `✅ Temp password generated for ${u.username}. Copy it now.`);
    } catch (err) {
      flashUserMsg(false, '❌ ' + (err.response?.data?.error || 'Failed'));
    }
  };

  const toggleUserAdmin = async (u) => {
    const makeAdmin = !u.is_admin;
    const verb = makeAdmin ? 'grant admin access to' : 'remove admin access from';
    if (!window.confirm(`Are you sure you want to ${verb} ${u.username}?`)) return;
    try {
      await API.put(`/auth/admin/users/${u.id}/admin`, { is_admin: makeAdmin });
      await loadManageUsers();
      flashUserMsg(true, `✅ ${u.username} is ${makeAdmin ? 'now an admin' : 'no longer an admin'}.`);
    } catch (err) {
      flashUserMsg(false, '❌ ' + (err.response?.data?.error || 'Failed'));
    }
  };

  const deactivateUser = async (u) => {
    if (!window.confirm(`Deactivate ${u.username}? They won't be able to log in but their picks, history, and badges are all preserved. You can restore them anytime.`)) return;
    try {
      await API.post(`/auth/admin/users/${u.id}/deactivate`);
      await loadManageUsers();
      flashUserMsg(true, `✅ ${u.username} deactivated.`);
    } catch (err) {
      flashUserMsg(false, '❌ ' + (err.response?.data?.error || 'Failed'));
    }
  };

  const reactivateUser = async (u) => {
    try {
      await API.post(`/auth/admin/users/${u.id}/reactivate`);
      await loadManageUsers();
      flashUserMsg(true, `✅ ${u.username} restored.`);
    } catch (err) {
      flashUserMsg(false, '❌ ' + (err.response?.data?.error || 'Failed'));
    }
  };

  const dismissTempPassword = (userId) => {
    setTempPasswords(prev => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
    // Issued again later, it should scroll into view again.
    scrolledToTemp.current.delete(userId);
  };

  useEffect(() => {
    const el = userListRef.current;
    if (!el) { setUserListScrolls(false); return; }
    // Rows grow when the buttons wrap, so a width change can turn a list that
    // fitted into one that doesn't.
    const check = () => setUserListScrolls(el.scrollHeight > el.clientHeight + 1);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [manageUsers, tempPasswords]);

  const loadPoolMembers = async (poolId) => {
    try {
      const res = await API.get(`/pools/${poolId}`);
      setPoolMembersCache(prev => ({ ...prev, [poolId]: res.data.members || [] }));
    } catch (e) {}
  };

  const createAdminPool = async () => {
    const name = newPoolName.trim();
    if (!name) return;
    try {
      const res = await API.post('/pools', { name });
      setPoolMsg({ ok: true, text: `✅ "${res.data.pool.name}" created (code ${res.data.pool.invite_code})` });
      setNewPoolName('');
      await loadPoolAdminData();
    } catch (err) {
      setPoolMsg({ ok: false, text: '❌ ' + (err.response?.data?.error || 'Failed') });
    }
    setTimeout(() => setPoolMsg(null), 4000);
  };

  const loadSentLog = async () => {
    try {
      const res = await API.get('/pools/admin/broadcasts');
      setSentLog(res.data.broadcasts || []);
    } catch (e) {}
  };

  const sendBroadcast = async () => {
    const body = castText.trim();
    if (!body) return;
    const where = castPool === 'all'
      ? 'every live pool'
      : `"${(allPools.find(p => String(p.id) === String(castPool)) || {}).name || 'that pool'}"`;
    if (!window.confirm(
      `Post this in ${where}?\n\n"${body}"\n\n` +
      (castNotify ? 'Everyone in those pools gets a push notification.' : 'No push notification will be sent.')
    )) return;

    setCastBusy(true); setCastMsg(null);
    try {
      const res = await API.post('/pools/admin/broadcast', {
        message: body,
        notify: castNotify,
        ...(castPool === 'all' ? {} : { pool_ids: [Number(castPool)] })
      });
      const d = res.data;
      setCastMsg({ ok: true, text:
        `✅ Posted in ${d.posted} pool${d.posted === 1 ? '' : 's'}` +
        (castNotify ? `, ${d.notified} notified.` : '.') });
      setCastText('');
      await loadSentLog();
    } catch (err) {
      setCastMsg({ ok: false, text: '❌ ' + (err.response?.data?.error || 'Failed to send') });
    }
    setCastBusy(false);
    setTimeout(() => setCastMsg(null), 6000);
  };

  const assignUserToPool = async (poolId) => {
    const userId = assignSelect[poolId];
    if (!userId) return;
    try {
      await API.post(`/pools/${poolId}/members`, { user_id: parseInt(userId) });
      setAssignSelect(prev => ({ ...prev, [poolId]: '' }));
      await loadPoolMembers(poolId);
      await loadPoolAdminData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed');
    }
  };

  // Handing over a pool is the one action here that gives away powers rather
  // than using them, so it spells out what the new commissioner will be able to
  // do instead of just asking "are you sure?".
  const makeCommissioner = async (poolId, userId, username, poolName) => {
    const ok = window.confirm(
      `Make ${username} commissioner of "${poolName}"?\n\n` +
      `They'll be able to rename it, regenerate the invite code, remove members, ` +
      `turn survivor and double entry on or off, run confirmations, and delete the pool.\n\n` +
      `You keep full control of every pool as admin.`
    );
    if (!ok) return;
    try {
      const res = await API.put(`/pools/${poolId}/commissioner`, { user_id: userId });
      setPoolMsg({ ok: true, text: '✅ ' + res.data.message });
      await loadPoolMembers(poolId);
      await loadPoolAdminData();
    } catch (err) {
      setPoolMsg({ ok: false, text: err.response?.data?.error || 'Failed to change commissioner' });
    }
    setTimeout(() => setPoolMsg(null), 5000);
  };

  const removeAdminMember = async (poolId, userId, username) => {
    if (!window.confirm(`Remove ${username} from this pool?`)) return;
    try {
      await API.delete(`/pools/${poolId}/members/${userId}`);
      await loadPoolMembers(poolId);
      await loadPoolAdminData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed');
    }
  };

  const renameAdminPool = async (poolId) => {
    const name = renameInputs[poolId];
    if (!name?.trim()) return;
    try {
      await API.put(`/pools/${poolId}`, { name: name.trim() });
      setRenameInputs(prev => ({ ...prev, [poolId]: '' }));
      await loadPoolAdminData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed');
    }
  };

  /* End a pool without destroying it. Offered beside Delete on purpose: this is
     almost always the one you want, because deleting takes the weekly boards,
     the chat and every badge won in the pool with it. */
  const toggleArchivePool = async (pool) => {
    const archiving = !pool.archived_at;
    if (archiving && !window.confirm(
      `Archive "${pool.name}"?

It disappears from everyone's pool list and stops taking picks and new members. ` +
      `Its ${pool.member_count} members, results and badges are all kept, and you can reopen it later.`)) return;
    try {
      const r = await API.put(`/pools/${pool.id}/archive`, { archived: archiving });
      alert(r.data?.message || 'Done');
      await loadPoolAdminData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed');
    }
  };

  const deleteAdminPool = async (poolId, name) => {
    if (!window.confirm(`Delete "${name}"? This also wipes all its chat. This cannot be undone.`)) return;
    try {
      await API.delete(`/pools/${poolId}`);
      await loadPoolAdminData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed');
    }
  };

  const regenAdminCode = async (poolId) => {
    if (!window.confirm('Generate a new invite code? The old one stops working.')) return;
    try {
      const res = await API.post(`/pools/${poolId}/regenerate-code`);
      await loadPoolAdminData();
      alert(`New code: ${res.data.invite_code}`);
    } catch (err) {
      alert('Failed');
    }
  };

  /**
   * Choose the code rather than take a random one.
   *
   * The same endpoint the Pools page uses. It lives here too because this panel
   * already shows the code, renames the pool and regenerates the code — three
   * quarters of the job — so "set it to FREEPLAY" is exactly what someone
   * standing here expects to be able to do.
   */
  const setAdminCode = async (poolId) => {
    const wanted = codeInputs[poolId];
    if (!wanted?.trim()) return;
    try {
      const res = await API.put(`/pools/${poolId}/invite-code`, { invite_code: wanted });
      setCodeInputs(prev => ({ ...prev, [poolId]: '' }));
      await loadPoolAdminData();
      setPoolMsg({ ok: true, text: res.data.normalizedFrom
        ? `✅ Code set to ${res.data.invite_code}. No spaces allowed in the invite link, so "${res.data.normalizedFrom}" became ${res.data.invite_code}.`
        : `✅ Code set to ${res.data.invite_code}` });
    } catch (err) {
      setPoolMsg({ ok: false, text: err.response?.data?.error || 'Could not set that code' });
    }
    setTimeout(() => setPoolMsg(null), 6000);
  };

  const toggleAdminPoolExpand = (poolId) => {
    if (expandedAdminPool === poolId) {
      setExpandedAdminPool(null);
    } else {
      setExpandedAdminPool(poolId);
      if (!poolMembersCache[poolId]) loadPoolMembers(poolId);
    }
  };

  const loadSupport = async (all = supportAll) => {
    try {
      const res = await API.get('/support/requests', { params: all ? { all: '1' } : {} });
      setSupport({
        requests: res.data.requests || [],
        open: res.data.open || 0,
        contact: res.data.contact || null
      });
    } catch (e) {} finally { setSupportLoaded(true); }
  };

  const markSupportHandled = async (id, handled) => {
    try {
      await API.post(`/support/requests/${id}/handled`, { handled });
      loadSupport();
    } catch (e) {}
  };

  const loadDmThreads = async () => {
    try {
      const res = await API.get('/dm/admin/threads');
      setDmThreads(res.data.threads || []);
      setDmThreadsLoaded(true);
    } catch (e) {
      setDmThreadsLoaded(true);
    }
  };

  const openDmThread = async (t) => {
    setDmSelected({ userA: t.user_a, userB: t.user_b });
    setDmThreadLoading(true);
    setDmThreadMessages([]);
    try {
      const res = await API.get('/dm/admin/thread', {
        params: { a: t.user_a.id, b: t.user_b.id }
      });
      setDmThreadMessages(res.data.messages || []);
    } catch (e) {
      setDmThreadMessages([]);
    } finally {
      setDmThreadLoading(false);
    }
  };

  const closeDmThread = () => {
    setDmSelected(null);
    setDmThreadMessages([]);
  };

  const deleteDmMessage = async (msgId) => {
    if (!window.confirm('Delete this message for everyone? This cannot be undone.')) return;
    try {
      await API.delete(`/dm/messages/${msgId}`);
      setDmThreadMessages(prev => prev.filter(m => m.id !== msgId));
      // refresh thread list counts in background
      loadDmThreads();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed');
    }
  };

  const loadMemberships = async () => {
    try {
      const res = await API.get('/membership/admin/settings');
      setMem(res.data);
    } catch (e) { setMem(null); }
  };

  const togglePaywall = async (on) => {
    // What ON means depends on whether a start date is set, so the warning has
    // to say which — "everyone is blocked right now" and "nothing changes until
    // September" are very different things to be agreeing to.
    const scheduled = mem?.paywallStartsAt && Date.parse(mem.paywallStartsAt) > Date.now();
    const when = scheduled ? new Date(mem.paywallStartsAt).toLocaleString() : null;
    const msg = on
      ? (scheduled
          ? `Turn the paywall ON, starting ${when}?\n\nNobody is blocked before then. Everyone keeps playing free, and the countdown appears in the app so people can pay early.`
          : 'Turn the paywall ON with no start date? Everyone without a season membership is blocked from submitting picks IMMEDIATELY.')
      : 'Turn the paywall OFF? Everyone can submit picks again, paid or not.';
    if (!window.confirm(msg)) return;
    setMemBusy(true);
    try { await API.put('/membership/admin/paywall', { enabled: on }); await loadMemberships(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setMemBusy(false); }
  };

  // Schedule (or clear) the date enforcement begins. Sent with the paywall's
  // current enabled state so saving a date never flips the switch by accident.
  const savePaywallStart = async (localValue) => {
    setMemBusy(true);
    try {
      await API.put('/membership/admin/paywall', {
        enabled: !!mem?.paywallEnabled,
        // datetime-local has no timezone; it means the admin's own wall clock,
        // which is what they typed. new Date() reads it that way, toISOString
        // hands the server an unambiguous instant.
        starts_at: localValue ? new Date(localValue).toISOString() : null
      });
      await loadMemberships();
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setMemBusy(false); }
  };

  const toggleExempt = async (userId, exempt) => {
    setMemBusy(true);
    try { await API.put(`/membership/admin/exempt/${userId}`, { exempt }); await loadMemberships(); await loadManageUsers(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setMemBusy(false); }
  };

  const loadCurrentWeek = async () => {
    try {
      const res = await API.get('/games/current-week');
      setWeekInfo(res.data);
    } catch (e) {}
  };

  useEffect(() => { loadData(); loadCurrentWeek(); loadPoolAdminData(); loadManageUsers(); loadDmThreads(); loadMemberships(); loadSupport(); }, []);

  useEffect(() => {
    API.get('/stats/admin/overview').then(r => setCounts(r.data || null)).catch(() => {});
  }, []);

  useEffect(() => {
    API.get('/events/admin/funnel')
      .then(r => { setFunnel(r.data?.funnel || null); setFunnelSubs(r.data?.subscribers ?? null); })
      .catch(() => {});
  }, []);

  // Refreshed on a timer: the interesting moment is the ninety minutes before
  // kickoff, and a stale "waiting" would be read as "it never fired".
  const loadPushStatus = useCallback(() => {
    API.get('/push/admin/status').then(r => setPushStatus(r.data || null)).catch(() => {});
  }, []);
  useEffect(() => {
    loadPushStatus();
    const iv = setInterval(loadPushStatus, 60000);
    return () => clearInterval(iv);
  }, [loadPushStatus]);

  /* Pull the week's recap down as a file.
   *
   * The endpoint is admin-only and authenticates with a Bearer token, so the URL
   * cannot simply be opened in a new tab: an address bar sends no Authorization
   * header and gets a 401. It has to be fetched and turned into a download here.
   */
  const downloadRecap = async () => {
    setRecapBusy(true);
    setRecapMsg(null);
    try {
      const wk = recapWeek.trim();
      const r = await API.get('/recap/admin/week' + (wk ? `?week=${encodeURIComponent(wk)}` : ''),
        { responseType: 'blob' });
      // The server already named the file for the right week; trust it over
      // anything guessed here, which would be wrong whenever the week was left
      // blank and the server picked the latest one itself.
      const cd = r.headers['content-disposition'] || '';
      const name = /filename="([^"]+)"/.exec(cd)?.[1] || 'recap.md';
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setRecapMsg({ ok: true, text: `Downloaded ${name}.` });
    } catch (err) {
      /* responseType 'blob' applies to error responses too, so err.response.data
         is a Blob holding the JSON, not the parsed object. Reading .error off it
         gives undefined and the real message ("nothing to recap yet") is lost,
         which is the message that matters most for the whole of August. */
      let text = 'The recap could not be downloaded.';
      const data = err.response?.data;
      if (data instanceof Blob) {
        try { text = JSON.parse(await data.text()).error || text; } catch { /* keep the default */ }
      } else if (data?.error) {
        text = data.error;
      }
      setRecapMsg({ ok: false, text });
    } finally {
      setRecapBusy(false);
    }
  };

  /* Correct a mistyped address.
   *
   * Worth a click rather than a support conversation: a typo at signup makes an
   * account silently unreachable AND unrecoverable, because password reset is
   * email-only. The prompt is seeded with the current value so a one-character
   * fix is a one-character edit. */
  const fixUserEmail = async (u) => {
    const next = window.prompt(`Correct the email address on ${u.username}'s account.`, u.email || '');
    if (next === null) return;
    const trimmed = next.trim();
    if (!trimmed || trimmed === (u.email || '')) return;
    try {
      const r = await API.put(`/auth/admin/users/${u.id}/email`, { email: trimmed });
      const killed = r.data?.resetLinksInvalidated || 0;
      alert(r.data?.message +
        (killed ? `\n\n${killed} password reset link${killed === 1 ? '' : 's'} sent to the old address ${killed === 1 ? 'was' : 'were'} cancelled.` : ''));
      await loadManageUsers();
    } catch (e) {
      alert(e.response?.data?.error || 'That address could not be saved.');
    }
  };

  const syncSchedule = async () => {
    setSyncLoading(true);
    setSyncResult(null);
    try {
      const res = await API.post('/games/sync');
      setSyncResult({ success: true, msg: `✅ ${res.data.message} (${res.data.week})` });
      await loadData();
    } catch (err) {
      setSyncResult({ success: false, msg: '❌ ' + (err.response?.data?.error || 'Sync failed') });
    } finally {
      setSyncLoading(false);
    }
  };

  const toggleLock = async () => {
    try {
      await API.post('/games/lock', { locked: !lockStatus });
      setLockStatus(!lockStatus);
    } catch (e) {}
  };

  const setWinner = async (gameId) => {
    const input = scoreInputs[gameId] || {};
    if (!input.winner) return;
    try {
      await API.post(`/games/${gameId}/set-winner`, { winner_abbr: input.winner, home_score: parseInt(input.home) || 0, away_score: parseInt(input.away) || 0 });
      setSetWinnerResult(prev => ({ ...prev, [gameId]: '✅ Updated' }));
      await loadData();
      setTimeout(() => setSetWinnerResult(prev => ({ ...prev, [gameId]: '' })), 3000);
    } catch (err) {
      setSetWinnerResult(prev => ({ ...prev, [gameId]: '❌ Failed' }));
    }
  };

  const setCurrentWeek = async (weekId) => {
    try {
      const res = await API.post(`/games/set-current-week/${weekId}`);
      setSetCurrentResult({ id: weekId, msg: `✅ ${res.data.message}` });
      await loadData();
      setTimeout(() => setSetCurrentResult(null), 3000);
    } catch (err) {
      setSetCurrentResult({ id: weekId, msg: '❌ Failed' });
    }
  };

  return (
    <div style={s.page}>
      {/* Dropdown lists, made readable.
          A native <select className="admin-select"> paints its own popup, and the options do NOT inherit
          the dark background from the control: they take the browser default,
          which is white. With white text on top of that the names were invisible
          until the cursor moved over one and the highlight gave them something
          to sit on. Chrome only honours this when the option itself is styled,
          so it is a rule rather than an inline style on the control. */}
      <style>{`
        .admin-select option,
        .admin-select optgroup {
          background: #0d1526;
          color: #dde;
        }
      `}</style>
      <div style={s.title}>⚙️ Admin Panel</div>
      <div style={s.subtitle}>Manage your Rivalry Picks pools</div>

      <div style={s.grid}>

      {/* How many people are actually playing, and in how many pools.
          "Active" has no single honest answer, so this shows the ladder rather
          than picking one rung: signed up, joined something, played this week.
          The gaps are the story. Six ad signups joined nothing, and for as long
          as the only number anywhere was the length of the user list, that read
          as six new members. */}
      <div style={{ ...s.section, ...s.wide }}>
        <div style={s.sectionTitle}>📊 PLAYERS &amp; POOLS</div>
        <div style={s.sectionDesc}>
          {counts?.week
            ? <>Counted right now, for <strong style={{ color: '#99a' }}>{counts.week.label}</strong>. Closed accounts are left out of every number.</>
            : 'Closed accounts are left out of every number.'}
        </div>
        {!counts ? (
          <div style={{ fontSize: 13, color: '#556' }}>Loading...</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 28 }}>

            <div>
              <div style={{ ...s.label, marginBottom: 10 }}>PLAYERS</div>
              {[
                ['Picked this week', counts.players.pickedThisWeek,
                 'Put a pick in for ' + (counts.week?.label || 'the current week') + '. The closest thing to an active player.', true],
                ['Picked last week', counts.players.pickedLastWeek,
                 counts.prevWeek ? 'The same count for ' + counts.prevWeek.label + ', to compare against.' : 'No earlier week to compare with yet.'],
                ['Opened the app, 7 days', counts.players.opened7,
                 'Includes anyone who read the board and picked nothing.'],
                ['Opened the app, 30 days', counts.players.opened30, 'The wider window.'],
                ['In at least one pool', counts.players.inAPool, 'Holds a seat somewhere.'],
                ['Signed up, joined nothing', counts.players.poolless,
                 'Registered and in no pool at all. This is the leak.', counts.players.poolless > 0],
                ['Registered accounts', counts.players.registered, 'Everyone who can sign in.'],
                ['New this week', counts.players.joined7, 'Accounts created in the last 7 days.'],
                ['Closed accounts', counts.players.deactivated, 'Deactivated, and left out of everything above.'],
              ].map(([label, value, note, accent]) => (
                <div key={label} style={s.weekItem}>
                  <div style={{ minWidth: 0 }}>
                    <div style={s.weekName}>{label}</div>
                    <div style={s.weekMeta}>{note}</div>
                  </div>
                  <span style={{
                    fontFamily: "'Oswald', sans-serif", fontSize: 22, fontWeight: 700,
                    color: accent ? '#7fb3ff' : value ? '#99a' : '#445',
                    flexShrink: 0, marginLeft: 12
                  }}>{value ?? 0}</span>
                </div>
              ))}
            </div>

            {/* Money, and the gap between playing and paying. The row that
                matters before a deadline is "picked, has not paid": those people
                have done the work and are about to lose their place on the
                board, which makes them the most persuadable names you have. */}
            {counts.payments && (
              <div>
                <div style={{ ...s.label, marginBottom: 10 }}>PAID · {counts.payments.season}</div>
                {[
                  ['Picked, has not paid', counts.payments.pickedUnpaid,
                   'Made picks this week with no membership. They lose their place on the board when the paywall starts.',
                   counts.payments.pickedUnpaid > 0],
                  ['People who have paid', counts.payments.paidPeople,
                   'Distinct accounts holding a season pass. One payment, every pool they are in.', true],
                  ['People still owing', counts.payments.unpaid,
                   'In a live pool with no season pass against their name.',
                   counts.payments.unpaid > 0],
                  ['People in a live pool', counts.payments.owing,
                   'Everyone who could be paying. Archived pools are left out.'],
                  ['In more than one pool', counts.payments.multiPool,
                   'Under the old per-pool price this was extra revenue. Now it is the engagement number: people who liked it enough to join again.'],
                  ['Paid by card', counts.payments.stripeSeats, 'Through Stripe. Real money.'],
                  ['Comped', counts.payments.compedSeats, 'Granted by hand, no charge.'],
                  ['Plays free', counts.payments.exempt, 'Accounts marked exempt from the paywall.'],
                ].map(([label, value, note, accent]) => (
                  <div key={label} style={s.weekItem}>
                    <div style={{ minWidth: 0 }}>
                      <div style={s.weekName}>{label}</div>
                      <div style={s.weekMeta}>{note}</div>
                    </div>
                    <span style={{
                      fontFamily: "'Oswald', sans-serif", fontSize: 22, fontWeight: 700,
                      color: accent ? '#7fb3ff' : value ? '#99a' : '#445',
                      flexShrink: 0, marginLeft: 12
                    }}>{value ?? 0}</span>
                  </div>
                ))}
                <div style={{ ...s.weekItem, borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 6 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={s.weekName}>Taken this season</div>
                    <div style={s.weekMeta}>Card payments only. Comped seats are worth nothing.</div>
                  </div>
                  <span style={{
                    fontFamily: "'Oswald', sans-serif", fontSize: 22, fontWeight: 700,
                    color: '#4caf50', flexShrink: 0, marginLeft: 12
                  }}>${(counts.payments.revenueCents / 100).toFixed(0)}</span>
                </div>
              </div>
            )}

            <div>
              <div style={{ ...s.label, marginBottom: 10 }}>POOLS</div>
              {[
                ['Playing this week', counts.pools.playingThisWeek,
                 'Two or more members, and somebody picked. A real game.', true],
                ['Two or more members', counts.pools.filled, 'Enough people in it to be a competition.'],
                ['One member only', counts.pools.solo,
                 'A commissioner sitting alone, waiting for somebody to join.', counts.pools.solo > 0],
                ['Nobody in them', counts.pools.empty,
                 'Everyone left, or the last account was closed.', counts.pools.empty > 0],
                ['Total pools', counts.pools.total, 'Every pool that exists.'],
                ['Open to the public', counts.pools.public, 'Findable without an invite code.'],
                ['Created this week', counts.pools.created7, 'New in the last 7 days.'],
                ['Pools offering a 2nd entry', counts.pools.doubleEntryOn,
                 'Second entries are retired, so this should stay at zero.',
                 counts.pools.doubleEntryOn > 0],
                ['Second entries still held', counts.pools.secondEntries,
                 'Anything left was paid for and kept on purpose. Settle it by hand.',
                 counts.pools.secondEntries > 0],
                ['Picking with no pool', counts.pools.picksWithNoPool,
                 'Making picks from a personal seat, in no pool at all.',
                 counts.pools.picksWithNoPool > 0],
              ].map(([label, value, note, accent]) => (
                <div key={label} style={s.weekItem}>
                  <div style={{ minWidth: 0 }}>
                    <div style={s.weekName}>{label}</div>
                    <div style={s.weekMeta}>{note}</div>
                  </div>
                  <span style={{
                    fontFamily: "'Oswald', sans-serif", fontSize: 22, fontWeight: 700,
                    color: accent ? '#7fb3ff' : value ? '#99a' : '#445',
                    flexShrink: 0, marginLeft: 12
                  }}>{value ?? 0}</span>
                </div>
              ))}
            </div>

          </div>
        )}
      </div>

      {/* The week written up as prose, for feeding to something that reads it
          aloud. This is here rather than in a script because the alternative was
          copying the database off the Railway volume every week, and a weekly
          chore that annoying is a weekly chore that stops happening in October. */}
      <div style={s.section}>
        <div style={s.sectionTitle}>📝 WEEKLY RECAP</div>
        <div style={s.sectionDesc}>
          The week as a story rather than a table: who won, how many they beat, what the
          tiebreaker settled, and the game that wrecked everybody. Downloads as a file.
          It names people and quotes their picks, so it is not something to post publicly.
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={s.label} htmlFor="recap-week">WEEK</label>
            <input
              id="recap-week"
              style={{ ...s.input, width: 110 }}
              value={recapWeek}
              onChange={e => setRecapWeek(e.target.value.replace(/[^0-9]/g, ''))}
              placeholder="Latest"
              inputMode="numeric"
            />
          </div>
          <button
            style={{ ...s.btn, ...s.btnPrimary, marginBottom: 0, opacity: recapBusy ? 0.7 : 1 }}
            onClick={downloadRecap}
            disabled={recapBusy}
          >{recapBusy ? 'Building...' : '⬇ Download recap'}</button>
        </div>
        <div style={{ color: '#556', fontSize: 12.5, marginTop: 10 }}>
          Leave the week blank for the most recent one with scored picks. Before Week 1 has
          been played there is nothing to recap, and it will say so.
        </div>
        {recapMsg && (
          <div style={{ ...s.result, ...(recapMsg.ok ? s.resultSuccess : s.resultError) }}>
            {recapMsg.text}
          </div>
        )}
      </div>

      {/* Sync from ESPN — what it currently thinks the week is, and the button
          to go and check. These were two separate cards saying halves of the
          same thing, the second of which still told you to wait for the 2026
          schedule to be released. */}
      <div style={s.section}>
        <div style={s.sectionTitle}>📡 ESPN SYNC</div>
        {weekInfo && (
          <div style={{ color: '#aab', fontSize: 14, marginBottom: 10 }}>
            Season <strong style={{ color: '#fff' }}>{weekInfo.season}</strong> &nbsp;·&nbsp;
            Week <strong style={{ color: '#fff' }}>{weekInfo.week}</strong> &nbsp;·&nbsp;
            <strong style={{ color: '#fff' }}>{weekInfo.seasonType === 1 ? 'Preseason' : weekInfo.seasonType === 2 ? 'Regular Season' : weekInfo.seasonType === 3 ? 'Postseason' : 'Off-Season'}</strong>
          </div>
        )}
        <div style={s.sectionDesc}>
          Runs on its own every 10 minutes on game days and every 6 hours otherwise.
          Use this to pull schedule or scores immediately.
        </div>
        <button style={{ ...s.btn, ...s.btnPrimary, opacity: syncLoading ? 0.7 : 1 }} onClick={syncSchedule} disabled={syncLoading}>
          {syncLoading ? '⏳ Syncing...' : '📡 Sync Now'}
        </button>
        {syncResult && <div style={{ ...s.result, ...(syncResult.success ? s.resultSuccess : s.resultError) }}>{syncResult.msg}</div>}
      </div>

      {/* Lock picks */}
      {week && (
        <div style={s.section}>
          <div style={s.sectionTitle}>🔒 PICKS LOCK</div>
          <div style={s.sectionDesc}>Lock picks to prevent changes. Individual games auto-lock when they go from "scheduled" to "in progress".</div>
          <div style={{ marginBottom: 12 }}>
            <span style={{ fontSize: 14, color: '#aab' }}>
              Current status: &nbsp;
              <strong style={{ color: lockStatus ? '#ff8888' : '#4caf50' }}>{lockStatus ? '🔒 LOCKED' : '🔓 OPEN'}</strong>
            </span>
          </div>
          <button style={{ ...s.btn, ...(lockStatus ? s.btnSuccess : s.btnDanger) }} onClick={toggleLock}>
            {lockStatus ? '🔓 Unlock All Picks' : '🔒 Lock All Picks'}
          </button>
        </div>
      )}

      {/* Pool management (admin-only) */}
      <div style={{ ...s.section, ...s.wide, border: '1px solid rgba(213,10,10,0.2)' }}>
        <div style={s.sectionTitle}>🏆 POOL MANAGEMENT</div>
        <div style={s.sectionDesc}>
          Only admins can create pools. Assign players to pools directly (no invite code needed), rename pools, delete them, or kick members.
        </div>

        {/* Create pool */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 18 }}>
          <div style={s.gameField}>
            <label style={s.label}>NEW POOL NAME</label>
            <input style={{ ...s.input, width: 260 }} value={newPoolName} onChange={e => setNewPoolName(e.target.value)} onKeyDown={e => e.key === 'Enter' && createAdminPool()} placeholder="e.g. Office Picks 2026" maxLength={50} />
          </div>
          <button style={{ ...s.btn, ...s.btnPrimary, marginTop: 18 }} onClick={createAdminPool} disabled={!newPoolName.trim()}>
            + Create Pool
          </button>
        </div>
        {poolMsg && <div style={{ ...s.result, ...(poolMsg.ok ? s.resultSuccess : s.resultError) }}>{poolMsg.text}</div>}

        {/* Pool list */}
        {allPools.length === 0 ? (
          <div style={{ color: '#556', fontSize: 13, marginTop: 8 }}>No pools exist yet. Create one above.</div>
        ) : (
          <div style={{ marginTop: 14 }}>
            {allPools.map(pool => {
              const expanded = expandedAdminPool === pool.id;
              const members = poolMembersCache[pool.id] || [];
              const memberIds = new Set(members.map(m => m.id));
              const assignable = allUsers.filter(u => !memberIds.has(u.id));
              return (
                <div key={pool.id} style={{ ...s.weekItem, flexDirection: 'column', alignItems: 'stretch', gap: 10, paddingBottom: expanded ? 14 : 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={s.weekName}>{pool.name}</div>
                      <div style={s.weekMeta}>
                        {pool.member_count} member{pool.member_count !== 1 ? 's' : ''} &nbsp;·&nbsp;
                        Commish: {pool.commissioner_name} &nbsp;·&nbsp;
                        Code: <code style={{ background: 'rgba(127,179,255,0.1)', padding: '1px 6px', borderRadius: 4, color: '#7fb3ff' }}>{pool.invite_code}</code>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button style={{ ...s.btn, padding: '6px 12px', background: 'rgba(127,179,255,0.15)', border: '1px solid rgba(127,179,255,0.3)', color: '#7fb3ff', marginBottom: 0 }} onClick={() => toggleAdminPoolExpand(pool.id)}>
                        {expanded ? '▲ Collapse' : '⚙️ Manage'}
                      </button>
                      <button
                        style={{ ...s.btn, padding: '6px 12px', marginBottom: 0,
                          background: pool.archived_at ? 'rgba(76,175,80,0.15)' : 'rgba(255,255,255,0.06)',
                          border: '1px solid ' + (pool.archived_at ? 'rgba(76,175,80,0.4)' : 'rgba(255,255,255,0.14)'),
                          color: pool.archived_at ? '#4caf50' : '#aab' }}
                        onClick={() => toggleArchivePool(pool)}
                        title={pool.archived_at ? 'Put this pool back in circulation' : 'End this pool but keep everything in it'}
                      >{pool.archived_at ? '↩ Reopen' : '📦 Archive'}</button>
                      <button style={{ ...s.btn, ...s.btnDanger, padding: '6px 12px', marginBottom: 0 }} onClick={() => deleteAdminPool(pool.id, pool.name)}>🗑 Delete</button>
                    </div>
                  </div>

                  {expanded && (
                    <div style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: 12 }}>
                      {/* Rename + regen code */}
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                        <input
                          style={{ ...s.input, width: 220 }}
                          placeholder="Rename pool..."
                          value={renameInputs[pool.id] || ''}
                          onChange={e => setRenameInputs(prev => ({ ...prev, [pool.id]: e.target.value }))}
                          maxLength={50}
                        />
                        <button style={{ ...s.btn, ...s.btnPrimary, padding: '7px 14px', marginBottom: 0 }} onClick={() => renameAdminPool(pool.id)} disabled={!renameInputs[pool.id]?.trim()}>Rename</button>
                        <button style={{ ...s.btn, padding: '7px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#aab', marginBottom: 0 }} onClick={() => regenAdminCode(pool.id)}>🔄 New Code</button>
                        <input
                          style={{ ...s.input, width: 160, textTransform: 'uppercase' }}
                          placeholder="Custom code..."
                          value={codeInputs[pool.id] || ''}
                          onChange={e => setCodeInputs(prev => ({ ...prev, [pool.id]: e.target.value }))}
                          maxLength={14}
                        />
                        <button
                          style={{ ...s.btn, padding: '7px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#aab', marginBottom: 0 }}
                          onClick={() => setAdminCode(pool.id)}
                          disabled={(codeInputs[pool.id] || '').replace(/[\s-]+/g, '').length < 4}
                        >Set Code</button>
                      </div>
                      <div style={{ fontSize: 11, color: '#445', marginTop: -6, marginBottom: 12 }}>
                        4 to 12 letters and numbers. Spaces are dropped, so "Free Play" becomes FREEPLAY.
                        an invite link can't carry them.
                      </div>

                      {/* Assign user */}
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
                        <label style={{ fontSize: 12, color: '#889', fontWeight: 600, letterSpacing: 0.5 }}>ADD USER:</label>
                        <select className="admin-select"
                          style={{ ...s.select, minWidth: 200 }}
                          value={assignSelect[pool.id] || ''}
                          onChange={e => setAssignSelect(prev => ({ ...prev, [pool.id]: e.target.value }))}
                        >
                          <option value="">{assignable.length === 0 ? 'All users already in pool' : 'Choose a user...'}</option>
                          {assignable.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
                        </select>
                        <button
                          style={{ ...s.btn, ...s.btnSuccess, padding: '7px 14px', marginBottom: 0 }}
                          onClick={() => assignUserToPool(pool.id)}
                          disabled={!assignSelect[pool.id]}
                        >
                          Assign
                        </button>
                      </div>

                      {/* Members list */}
                      <div style={{ fontSize: 12, color: '#667', fontWeight: 700, letterSpacing: 0.5, marginBottom: 2 }}>MEMBERS ({members.length})</div>
                      <div style={{ fontSize: 11, color: '#445', marginBottom: 8 }}>
                        ⭐ is the commissioner. ☆ hands them the pool. They run it, you still oversee it.
                      </div>
                      {members.length === 0 ? (
                        <div style={{ color: '#445', fontSize: 13 }}>Loading members…</div>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {members.map(m => (
                            <div key={m.id} style={{
                              background: m.is_commissioner ? 'rgba(213,10,10,0.12)' : 'rgba(255,255,255,0.06)',
                              border: m.is_commissioner ? '1px solid rgba(213,10,10,0.25)' : '1px solid rgba(255,255,255,0.08)',
                              borderRadius: 20,
                              padding: '4px 10px',
                              fontSize: 12,
                              color: m.is_commissioner ? '#ff9999' : '#aab',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6
                            }}>
                              {m.is_commissioner ? '⭐ ' : ''}{m.username}
                              {!m.is_commissioner && (
                                <>
                                  <button
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b8860b', fontSize: 13, padding: 0, lineHeight: 1 }}
                                    onClick={() => makeCommissioner(pool.id, m.id, m.username, pool.name)}
                                    title={`Make ${m.username} commissioner of ${pool.name}`}
                                  >☆</button>
                                  <button
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f44336', fontSize: 14, padding: 0, lineHeight: 1 }}
                                    onClick={() => removeAdminMember(pool.id, m.id, m.username)}
                                    title={`Remove ${m.username}`}
                                  >×</button>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* User management (admin-only) */}
      <div style={{ ...s.section, ...s.wide, border: '1px solid rgba(213,10,10,0.2)' }}>
        <div style={s.sectionTitle}>👥 USER MANAGEMENT</div>
        <div style={s.sectionDesc}>
          Reset passwords, toggle admin access, or deactivate accounts. Deactivation is reversible. All picks, history, and badges are preserved.
        </div>
        {userMsg && <div style={{ ...s.result, ...(userMsg.ok ? s.resultSuccess : s.resultError), marginBottom: 12 }}>{userMsg.text}</div>}

        {manageUsers.length === 0 ? (
          <div style={{ color: '#556', fontSize: 13 }}>No users loaded yet.</div>
        ) : (
          <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#667', letterSpacing: 1 }}>
              {manageUsers.length} ACCOUNT{manageUsers.length === 1 ? '' : 'S'}
            </span>
            {userListScrolls && (
              <span style={{ fontSize: 11, color: '#556' }}>scroll the list for the rest</span>
            )}
          </div>
          <div
            ref={userListRef}
            style={{
              display: 'flex', flexDirection: 'column', gap: 8,
              // Tall enough to read several accounts at once, short enough that
              // the sections below it are reachable without a long scroll past.
              maxHeight: 'min(58vh, 440px)',
              overflowY: 'auto',
              // Keeps the scrollbar off the row borders.
              paddingRight: 6
            }}
          >
            {manageUsers.map(u => {
              const isSelf = u.id === selfId;
              const inactive = !u.is_active;
              return (
                <div
                  key={u.id}
                  style={{
                    background: inactive ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.04)',
                    border: '1px solid ' + (inactive ? 'rgba(255,136,136,0.2)' : 'rgba(255,255,255,0.08)'),
                    borderRadius: 8,
                    padding: '12px 14px',
                    opacity: inactive ? 0.65 : 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ flexGrow: 1, flexBasis: 200, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#dde', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span title={u.username} style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: inactive ? 'line-through' : 'none' }}>{u.username}</span>
                        {u.is_admin && (
                          <span style={{ padding: '2px 8px', borderRadius: 10, background: 'rgba(255,215,0,0.15)', border: '1px solid rgba(255,215,0,0.3)', color: '#FFD700', fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>⭐ ADMIN</span>
                        )}
                        {inactive && (
                          <span style={{ padding: '2px 8px', borderRadius: 10, background: 'rgba(213,10,10,0.15)', border: '1px solid rgba(213,10,10,0.3)', color: '#ff8888', fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>DEACTIVATED</span>
                        )}
                        {isSelf && (
                          <span style={{ padding: '2px 8px', borderRadius: 10, background: 'rgba(127,179,255,0.15)', color: '#7fb3ff', fontSize: 10, fontWeight: 700, letterSpacing: 0.5 }}>YOU</span>
                        )}
                      </div>
                      <div title={u.email} style={{ fontSize: 12, color: '#667', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {u.email}
                        {u.deactivated_at && <> &nbsp;·&nbsp; deactivated {new Date(u.deactivated_at).toLocaleDateString()}</>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {/* Available on deactivated accounts too. A typo is often
                          the reason somebody looked dormant enough to deactivate
                          in the first place. */}
                      <button
                        style={{ ...s.btnSmall, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', color: '#aab' }}
                        onClick={() => fixUserEmail(u)}
                        title="Correct a mistyped email address"
                      >✉️ Fix Email</button>
                      {!inactive && (
                        <button
                          style={{ ...s.btnSmall, background: 'rgba(127,179,255,0.15)', border: '1px solid rgba(127,179,255,0.3)', color: '#7fb3ff' }}
                          onClick={() => resetUserPassword(u)}
                        >🔑 Reset PW</button>
                      )}
                      {!inactive && (
                        <button
                          style={{
                            ...s.btnSmall,
                            background: u.is_admin ? 'rgba(255,255,255,0.06)' : 'rgba(255,215,0,0.15)',
                            border: u.is_admin ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(255,215,0,0.3)',
                            color: u.is_admin ? '#aab' : '#FFD700',
                            opacity: isSelf && u.is_admin ? 0.5 : 1,
                            cursor: isSelf && u.is_admin ? 'not-allowed' : 'pointer'
                          }}
                          onClick={() => !(isSelf && u.is_admin) && toggleUserAdmin(u)}
                          disabled={isSelf && u.is_admin}
                          title={isSelf && u.is_admin ? "You can't remove your own admin access" : ''}
                        >{u.is_admin ? '⬇ Demote' : '⭐ Make Admin'}</button>
                      )}
                      {/* The only place in the app that can reach ANYBODY.
                          The server has always allowed it, because the support
                          account is exempt from the shared-pool rule, but the
                          only buttons were beside people you already shared a
                          pool with. That left roughly half the accounts on the
                          site unreachable through the interface. */}
                      {!isSelf && (
                        <button
                          style={{ ...s.btnSmall, background: 'rgba(127,179,255,0.15)', border: '1px solid rgba(127,179,255,0.35)', color: '#7fb3ff' }}
                          onClick={() => setDmUserId(u.id)}
                          title={`Message ${u.username}`}
                        >💬 Message</button>
                      )}
                      {inactive ? (
                        <button
                          style={{ ...s.btnSmall, background: 'rgba(76,175,80,0.15)', border: '1px solid rgba(76,175,80,0.3)', color: '#4caf50' }}
                          onClick={() => reactivateUser(u)}
                        >♻ Reactivate</button>
                      ) : (
                        <button
                          style={{
                            ...s.btnSmall,
                            background: 'rgba(213,10,10,0.15)',
                            border: '1px solid rgba(213,10,10,0.3)',
                            color: '#ff8888',
                            opacity: isSelf ? 0.5 : 1,
                            cursor: isSelf ? 'not-allowed' : 'pointer'
                          }}
                          onClick={() => !isSelf && deactivateUser(u)}
                          disabled={isSelf}
                          title={isSelf ? "You can't deactivate your own account" : ''}
                        >🚫 Deactivate</button>
                      )}
                      <button
                        style={{ ...s.btnSmall, background: u.membership_exempt ? 'rgba(127,179,255,0.18)' : 'rgba(255,255,255,0.06)', border: u.membership_exempt ? '1px solid rgba(127,179,255,0.4)' : '1px solid rgba(255,255,255,0.12)', color: u.membership_exempt ? '#7fb3ff' : '#889' }}
                        onClick={() => toggleExempt(u.id, !u.membership_exempt)}
                        disabled={memBusy}
                        title={u.membership_exempt ? 'Remove free access' : 'Let this user play without paying'}
                      >{u.membership_exempt ? '✓ Plays free' : 'Comp'}</button>
                    </div>
                  </div>
                  {tempPasswords[u.id] && (
                    <div ref={el => {
                      // Once, on appearing: the list is clipped now, and a
                      // password shown below the fold reads as nothing having
                      // happened.
                      if (el && !scrolledToTemp.current.has(u.id)) {
                        scrolledToTemp.current.add(u.id);
                        el.scrollIntoView({ block: 'nearest' });
                      }
                    }} style={{
                      background: 'rgba(255,215,0,0.1)',
                      border: '1px solid rgba(255,215,0,0.35)',
                      borderRadius: 8,
                      padding: '10px 12px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 10,
                      flexWrap: 'wrap'
                    }}>
                      <div style={{ fontSize: 13 }}>
                        <div style={{ color: '#FFD700', fontWeight: 700, marginBottom: 2 }}>One-time temp password for {u.username}:</div>
                        <code style={{ fontSize: 16, color: '#fff', background: 'rgba(0,0,0,0.4)', padding: '4px 10px', borderRadius: 6, letterSpacing: 2, fontWeight: 700 }}>
                          {tempPasswords[u.id]}
                        </code>
                        <div style={{ fontSize: 11, color: '#aab', marginTop: 6 }}>Share it securely with {u.username}. They should change it on next login.</div>
                      </div>
                      <button
                        style={{ ...s.btnSmall, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#aab' }}
                        onClick={() => {
                          navigator.clipboard?.writeText(tempPasswords[u.id]);
                          flashUserMsg(true, '📋 Copied to clipboard');
                        }}
                      >📋 Copy</button>
                      <button
                        style={{ ...s.btnSmall, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#889' }}
                        onClick={() => dismissTempPassword(u.id)}
                      >✕ Dismiss</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>

      {/* Email Invites */}
      <div style={{ ...s.section, border: '1px solid rgba(127,179,255,0.2)' }}>
        <div style={s.sectionTitle}>✉️ SEND INVITES</div>
        <div style={s.sectionDesc}>
          Send a branded invite email with the pool's invite code directly to your members. Requires GMAIL_USER and GMAIL_APP_PASSWORD set in Railway Variables.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={s.label}>SELECT POOL</label>
            <select className="admin-select"
              style={{ ...s.select, width: '100%', maxWidth: 360 }}
              value={invitePoolId}
              onChange={e => setInvitePoolId(e.target.value)}
            >
              <option value=''>Choose a pool</option>
              {allPools.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.invite_code})</option>
              ))}
            </select>
          </div>
          <div>
            <label style={s.label}>EMAIL ADDRESSES <span style={{ color: '#445', fontWeight: 400 }}>(one per line or comma-separated)</span></label>
            <textarea
              style={{ ...s.input, width: '100%', maxWidth: 480, minHeight: 100, resize: 'vertical', fontFamily: 'monospace', fontSize: 13, boxSizing: 'border-box' }}
              placeholder={'john@example.com\njane@example.com\nbob@example.com'}
              value={inviteEmails}
              onChange={e => setInviteEmails(e.target.value)}
            />
          </div>
          <div>
            <button
              style={{ ...s.btn, background: 'linear-gradient(135deg,#013369,#0a5299)', color: '#fff', opacity: inviteSending ? 0.7 : 1 }}
              disabled={inviteSending || !invitePoolId || !inviteEmails.trim()}
              onClick={async () => {
                setInviteSending(true);
                setInviteMsg(null);
                const emails = inviteEmails.split(/[\n,]+/).map(e => e.trim()).filter(Boolean);
                try {
                  const res = await API.post('/invites/send', { pool_id: parseInt(invitePoolId), emails });
                  setInviteMsg({ ok: true, text: '✅ ' + res.data.message });
                  setInviteEmails('');
                } catch (err) {
                  setInviteMsg({ ok: false, text: '❌ ' + (err.response?.data?.error || 'Failed to send') });
                } finally { setInviteSending(false); }
              }}
            >
              {inviteSending ? 'Sending...' : '✉️ SEND INVITES'}
            </button>
          </div>
          {inviteMsg && (
            <div style={{ ...s.result, ...(inviteMsg.ok ? s.resultSuccess : s.resultError) }}>{inviteMsg.text}</div>
          )}
        </div>
      </div>

      {/* Paywall / memberships (admin-only) */}
      <div style={{ ...s.section, ...s.wide, border: mem?.paywallActive ? '1px solid rgba(76,175,80,0.3)' : '1px solid rgba(255,179,71,0.3)' }}>
        <div style={s.sectionTitle}>💳 SEASON MEMBERSHIPS</div>
        <div style={s.sectionDesc}>
          The paywall gates <strong>submitting picks</strong>. While it's off, everyone plays free.
          Admins and comped users always play free. Pool money is separate and handled off-app.
        </div>

        {!mem ? (
          <div style={{ color: '#556', fontSize: 13 }}>Loading…</div>
        ) : (
          <>
            {/* Three states, not two: OFF, SCHEDULED (on but not yet biting),
                and ON. The middle one is the whole point of the start date —
                announced and payable, but nobody blocked. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
              {(() => {
                const st = mem.paywallActive
                  ? { text: 'PAYWALL ON, picks are gated', c: '#81c784', bg: 'rgba(76,175,80,0.16)', b: 'rgba(76,175,80,0.4)' }
                  : mem.paywallPending
                    ? { text: 'SCHEDULED, everyone free until it starts', c: '#7fb3ff', bg: 'rgba(127,179,255,0.14)', b: 'rgba(127,179,255,0.4)' }
                    : { text: 'PAYWALL OFF, everyone free', c: '#ffb347', bg: 'rgba(255,179,71,0.14)', b: 'rgba(255,179,71,0.35)' };
                return (
                  <span style={{ fontSize: 12, fontWeight: 700, padding: '5px 12px', borderRadius: 12,
                                 background: st.bg, border: '1px solid ' + st.b, color: st.c }}>{st.text}</span>
                );
              })()}
              <button
                style={{ ...s.btnSmall, background: mem.paywallEnabled ? 'rgba(255,179,71,0.15)' : 'rgba(76,175,80,0.2)', border: mem.paywallEnabled ? '1px solid rgba(255,179,71,0.35)' : '1px solid rgba(76,175,80,0.4)', color: mem.paywallEnabled ? '#ffb347' : '#4caf50' }}
                disabled={memBusy}
                onClick={() => togglePaywall(!mem.paywallEnabled)}
              >{mem.paywallEnabled ? 'Turn paywall OFF' : 'Turn paywall ON'}</button>
              <span style={{ fontSize: 12, color: '#667' }}>Season {mem.season}</span>
            </div>

            {/* Start date. datetime-local has no timezone — it's the admin's own
                wall clock, converted on save. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
              <span style={{ fontSize: 12, color: '#889', fontWeight: 600 }}>STARTS</span>
              <input
                type="datetime-local"
                defaultValue={mem.paywallStartsAt
                  ? (() => { const d = new Date(mem.paywallStartsAt);
                             const p = n => String(n).padStart(2, '0');
                             return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; })()
                  : ''}
                key={mem.paywallStartsAt || 'none'}
                onBlur={e => {
                  const cur = mem.paywallStartsAt
                    ? new Date(mem.paywallStartsAt).getTime() : null;
                  const next = e.target.value ? new Date(e.target.value).getTime() : null;
                  if (cur !== next) savePaywallStart(e.target.value);
                }}
                disabled={memBusy}
                style={{ padding: '7px 10px', borderRadius: 7, background: '#0d1525',
                         border: '1px solid rgba(255,255,255,0.12)', color: '#ccd', fontSize: 13 }}
              />
              {mem.paywallStartsAt && (
                <button style={{ ...s.btnSmall }} disabled={memBusy}
                        onClick={() => savePaywallStart('')}>Clear</button>
              )}
              <span style={{ fontSize: 12, color: '#667' }}>
                {mem.paywallStartsAt
                  ? (mem.paywallActive ? 'already passed' : 'free until then; people can still pay early')
                  : 'no date, ON blocks picks immediately'}
              </span>
            </div>

            {!mem.checkoutConfigured && (
              <div style={{ ...s.result, ...s.resultError, marginBottom: 12 }}>
                ⚠️ Stripe isn't configured. Set STRIPE_SECRET_KEY and STRIPE_PRICE_ID in Railway.
                Nobody can pay until then, so don't turn the paywall on yet.
              </div>
            )}

            <div style={{ fontSize: 11, color: '#667', fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>
              PAID THIS SEASON ({mem.memberships.length})
            </div>
            {mem.memberships.length === 0 ? (
              <div style={{ color: '#556', fontSize: 13, marginBottom: 14 }}>No memberships yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14 }}>
                {mem.memberships.map(m => (
                  <div key={`${m.user_id}-${m.pool_id}`} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12.5, color: '#aab', padding: '5px 10px', background: 'rgba(255,255,255,0.03)', borderRadius: 6 }}>
                    <span><strong style={{ color: '#dde' }}>{m.username}</strong> · {m.pool_name}</span>
                    <span style={{ color: m.source === 'admin' ? '#ffb347' : '#81c784' }}>
                      {m.source === 'admin' ? 'granted' : 'paid'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ fontSize: 11, color: '#667', fontWeight: 700, letterSpacing: 1, marginBottom: 6 }}>
              PLAYS FREE ({mem.exemptUsers.length})
            </div>
            {mem.exemptUsers.length === 0 ? (
              <div style={{ color: '#556', fontSize: 13 }}>Nobody comped. (Admins always play free.)</div>
            ) : (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {mem.exemptUsers.map(u => (
                  <span key={u.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(127,179,255,0.1)', border: '1px solid rgba(127,179,255,0.25)', borderRadius: 20, padding: '4px 10px', fontSize: 12, color: '#7fb3ff' }}>
                    {u.username}
                    <button
                      onClick={() => toggleExempt(u.id, false)}
                      disabled={memBusy}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#f44336', fontSize: 14, padding: 0, lineHeight: 1 }}
                      title="Remove free access"
                    >x</button>
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Support inbox — only the signed-out messages land here. The signed-in
          ones are DMs, and they arrive in your own thread list like any other. */}
      <div style={{ ...s.section, ...s.wide, border: '1px solid rgba(127,179,255,0.22)' }}>
        <div style={s.sectionTitle}>
          📮 SUPPORT INBOX
          {support.open > 0 && (
            <span style={{
              marginLeft: 10, fontSize: 12, padding: '3px 9px', verticalAlign: 'middle',
              background: 'linear-gradient(135deg,#D50A0A,#a00808)', color: '#fff',
              borderRadius: 12, letterSpacing: 0.5
            }}>{support.open} open</span>
          )}
        </div>
        <div style={s.sectionDesc}>
          Messages from people who were not signed in when they wrote, so they had
          no account to DM from. Anyone signed in reaches you as a normal DM
          instead, and those show up in your own messages.
          {support.contact
            ? ` Right now that's ${support.contact.username}.`
            : ' No active admin is set to receive them, so only this list is filling up.'}
        </div>

        <div style={{ marginBottom: 12 }}>
          <button
            onClick={() => { const next = !supportAll; setSupportAll(next); loadSupport(next); }}
            style={{
              padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#aab'
            }}
          >{supportAll ? 'Show open only' : 'Show everything'}</button>
        </div>

        {!supportLoaded ? (
          <div style={{ color: '#556', fontSize: 13 }}>Loading…</div>
        ) : support.requests.length === 0 ? (
          <div style={{ color: '#556', fontSize: 13 }}>
            {supportAll ? 'Nobody has used the contact form yet.' : 'Nothing open.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {support.requests.map(r => (
              <div key={r.id} style={{
                background: 'rgba(0,0,0,0.28)',
                border: '1px solid ' + (r.handled_at ? 'rgba(255,255,255,0.06)' : 'rgba(127,179,255,0.22)'),
                borderRadius: 10, padding: '12px 14px', opacity: r.handled_at ? 0.6 : 1
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                  <a href={`mailto:${r.email}`} style={{ color: '#7fb3ff', fontSize: 13, fontWeight: 700 }}>
                    {r.email}
                  </a>
                  <span style={{ color: '#556', fontSize: 11.5 }}>
                    {new Date(r.created_at + 'Z').toLocaleString()}
                  </span>
                </div>
                <div style={{ color: '#ccd', fontSize: 13.5, lineHeight: 1.6, margin: '8px 0', whiteSpace: 'pre-wrap' }}>
                  {r.message}
                </div>
                {r.user_agent && (
                  <div style={{ color: '#445', fontSize: 11, marginBottom: 8, wordBreak: 'break-all' }}>
                    {r.user_agent}
                  </div>
                )}
                <button
                  onClick={() => markSupportHandled(r.id, !r.handled_at)}
                  style={{
                    padding: '5px 11px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    background: r.handled_at ? 'rgba(255,255,255,0.06)' : 'rgba(76,175,80,0.15)',
                    border: '1px solid ' + (r.handled_at ? 'rgba(255,255,255,0.12)' : 'rgba(76,175,80,0.35)'),
                    color: r.handled_at ? '#aab' : '#81c784'
                  }}
                >{r.handled_at ? 'Reopen' : '✓ Handled'}</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Message every pool at once (admin-only) */}
      <div style={{ ...s.section, ...s.wide, border: '1px solid rgba(127,179,255,0.2)' }}>
        <div style={s.sectionTitle}>📣 MESSAGE THE POOLS</div>
        <div style={s.sectionDesc}>
          Posts into a pool's chat as you, in the room people already read. It can be replied to and
          a commissioner can delete it from their own pool, the same as any other message.
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
          <select className="admin-select"
            value={castPool}
            onChange={e => setCastPool(e.target.value)}
            style={{ ...s.select, minWidth: 200, flex: '0 1 240px', minHeight: 44 }}
          >
            <option value="all">Every live pool</option>
            {allPools.filter(p => !p.archived_at).map(p => (
              <option key={p.id} value={p.id}>{p.name} ({p.member_count})</option>
            ))}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: '#99a', cursor: 'pointer', minHeight: 44 }}>
            <input type="checkbox" checked={castNotify} onChange={e => setCastNotify(e.target.checked)} />
            Send a push notification
          </label>
        </div>

        <textarea
          value={castText}
          onChange={e => setCastText(e.target.value.slice(0, 500))}
          placeholder="What do they need to know?"
          rows={3}
          style={{ ...s.input, width: '100%', resize: 'vertical', boxSizing: 'border-box', marginBottom: 8 }}
        />
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={sendBroadcast}
            disabled={castBusy || !castText.trim()}
            style={{
              ...s.btn, minHeight: 44,
              opacity: (castBusy || !castText.trim()) ? 0.5 : 1,
              cursor: (castBusy || !castText.trim()) ? 'not-allowed' : 'pointer'
            }}
          >{castBusy ? 'Posting…' : '📣 Post it'}</button>
          {/* The same 500 the ordinary chat box enforces, counted down rather
              than discovered by being refused. */}
          <span style={{ fontSize: 12, color: castText.length > 450 ? '#ffa726' : '#556' }}>
            {500 - castText.length} left
          </span>
          {castNotify && (
            <span style={{ fontSize: 12, color: '#ffa726' }}>
              Phones will buzz. Anyone who turned reminders off stays off.
            </span>
          )}
        </div>
        {castMsg && (
          <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: castMsg.ok ? '#4caf50' : '#ff8888' }}>
            {castMsg.text}
          </div>
        )}

        {/* What was actually sent.
            A broadcast writes an ordinary message into every pool it targets,
            and those messages are scattered across rooms the sender cannot open
            at the same time. Without this there was no answer to "what did I
            send", which for the person who sent it is the same as not having
            sent it. */}
        <div style={{ marginTop: 22, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 16 }}>
          <div style={{ ...s.label, marginBottom: 10 }}>SENT</div>
          {sentLog.length === 0 ? (
            <div style={{ color: '#556', fontSize: 13 }}>Nothing sent yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sentLog.map(b => (
                <div key={b.id} style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 8, padding: '11px 14px'
                }}>
                  <div style={{ fontSize: 13.5, color: '#ccd', lineHeight: 1.5, marginBottom: 6 }}>
                    {b.message}
                  </div>
                  <div style={{ fontSize: 11.5, color: '#667', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <span>
                      {new Date(String(b.sentAt).replace(' ', 'T') + 'Z')
                        .toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </span>
                    <span>·</span>
                    <span>{b.poolCount} pool{b.poolCount === 1 ? '' : 's'}</span>
                    {/* Zero is worth printing. "Did anybody get a buzz" is the
                        question people ask after sending, and a blank leaves it
                        open. */}
                    <span>·</span>
                    <span style={{ color: b.notified > 0 ? '#7fb3ff' : '#556' }}>
                      {b.notified > 0 ? `${b.notified} notified` : 'no push'}
                    </span>
                    <span>·</span>
                    <span title={b.pools.map(p => p.name).join(', ')}
                          style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {b.pools.map(p => p.name).join(', ')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* DM Moderation (admin-only) */}
      <div style={{ ...s.section, ...s.wide, border: '1px solid rgba(213,10,10,0.2)' }}>
        <div style={s.sectionTitle}>💬 DIRECT MESSAGE MODERATION</div>
        <div style={s.sectionDesc}>
          Browse every 1:1 thread in the system. Open a conversation to review, or delete individual messages for both parties.
        </div>

        {!dmThreadsLoaded ? (
          <div style={{ color: '#556', fontSize: 13 }}>Loading threads…</div>
        ) : dmThreads.length === 0 ? (
          <div style={{ color: '#556', fontSize: 13 }}>No direct messages yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {dmThreads.map(t => {
              const key = `${t.user_a?.id}-${t.user_b?.id}`;
              return (
                <div
                  key={key}
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 8,
                    padding: '10px 14px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 10,
                    flexWrap: 'wrap'
                  }}
                >
                  <div style={{ flex: 1, minWidth: 220 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#dde' }}>
                      {t.user_a?.username || '-'}
                      <span style={{ color: '#556', margin: '0 8px' }}>↔</span>
                      {t.user_b?.username || '-'}
                    </div>
                    <div style={{ fontSize: 12, color: '#778', marginTop: 3, wordBreak: 'break-word' }}>
                      {t.last_snippet || <em style={{ color: '#445' }}>No preview</em>}
                    </div>
                    <div style={{ fontSize: 11, color: '#556', marginTop: 3 }}>
                      {t.message_count} message{t.message_count === 1 ? '' : 's'}
                      {t.last_message_at && <> · last {new Date(t.last_message_at + 'Z').toLocaleString()}</>}
                    </div>
                  </div>
                  <button
                    onClick={() => openDmThread(t)}
                    style={{
                      ...s.btnSmall,
                      background: 'rgba(127,179,255,0.15)',
                      border: '1px solid rgba(127,179,255,0.3)',
                      color: '#7fb3ff'
                    }}
                  >🔍 View</button>
                </div>
              );
            })}
          </div>
        )}

        {/* Thread viewer overlay */}
        {dmSelected && (
          <div
            onClick={closeDmThread}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
              zIndex: 1001,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 16
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background: '#0d1526',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 14,
                maxWidth: 640, width: '100%',
                maxHeight: '85vh',
                overflow: 'hidden',
                display: 'flex', flexDirection: 'column'
              }}
            >
              <div style={{
                padding: '14px 18px',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: 'linear-gradient(135deg, rgba(213,10,10,0.2), rgba(10,14,26,0.8))'
              }}>
                <div>
                  <div style={{ fontSize: 11, color: '#ff8888', letterSpacing: 2, fontFamily: "'Oswald', sans-serif", fontWeight: 700, textTransform: 'uppercase' }}>
                    Admin · DM Thread
                  </div>
                  <div style={{ fontSize: 18, fontFamily: "'Oswald', sans-serif", fontWeight: 700, color: '#fff', marginTop: 2 }}>
                    {dmSelected.userA.username} ↔ {dmSelected.userB.username}
                  </div>
                </div>
                <button
                  onClick={closeDmThread}
                  style={{
                    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                    color: '#aab', fontSize: 18, width: 34, height: 34,
                    borderRadius: 8, cursor: 'pointer', fontWeight: 700
                  }}
                >×</button>
              </div>
              <div style={{ padding: 14, overflowY: 'auto', flex: 1 }}>
                {dmThreadLoading ? (
                  <div style={{ color: '#556', fontSize: 13, textAlign: 'center', padding: 30 }}>Loading…</div>
                ) : dmThreadMessages.length === 0 ? (
                  <div style={{ color: '#556', fontSize: 13, textAlign: 'center', padding: 30 }}>No messages.</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {dmThreadMessages.map(m => (
                      <div
                        key={m.id}
                        style={{
                          display: 'flex', gap: 8, alignItems: 'flex-start',
                          padding: '6px 8px',
                          borderRadius: 6,
                          background: 'rgba(255,255,255,0.02)',
                          border: '1px solid rgba(255,255,255,0.05)'
                        }}
                      >
                        <span style={{ fontWeight: 700, color: '#7fb3ff', fontSize: 12, flexShrink: 0 }}>
                          {m.sender_username}:
                        </span>
                        <span style={{ color: '#dde', fontSize: 13, wordBreak: 'break-word', whiteSpace: 'pre-wrap', flex: 1 }}>
                          {m.message}
                        </span>
                        <span style={{ color: '#556', fontSize: 10, flexShrink: 0 }}>
                          {new Date(m.created_at + 'Z').toLocaleString()}
                        </span>
                        <button
                          onClick={() => deleteDmMessage(m.id)}
                          title="Delete (admin)"
                          style={{
                            background: 'none', border: 'none',
                            color: '#f44336', fontSize: 13, cursor: 'pointer',
                            opacity: 0.6, padding: '0 4px', lineHeight: 1
                          }}
                        >✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Week history */}
      {allWeeks.length > 0 && (
        <div style={{ ...s.section, ...s.wide }}>
          <div style={s.sectionTitle}>📅 ALL WEEKS</div>
          <div style={s.sectionDesc}>Switch the active week or view past week history.</div>
          {allWeeks.map(w => (
            <div key={w.id} style={s.weekItem}>
              <div>
                <div style={s.weekName}>{w.label}</div>
                <div style={s.weekMeta}>
                  {w.picks_locked ? '🔒 Picks locked' : '🔓 Picks open'} &nbsp;|&nbsp;
                  Season {w.season}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {w.is_current ? (
                  <span style={s.activeBadge}>✓ ACTIVE</span>
                ) : (
                  <button
                    style={{ ...s.btn, padding: '6px 14px', background: 'rgba(1,51,105,0.3)', border: '1px solid rgba(1,51,105,0.5)', color: '#7fb3ff', marginBottom: 0 }}
                    onClick={() => setCurrentWeek(w.id)}
                  >
                    Set Active
                  </button>
                )}
                {setCurrentResult && setCurrentResult.id === w.id && (
                  <span style={{ fontSize: 12, color: setCurrentResult.msg.startsWith('❌') ? '#ff8888' : '#4caf50' }}>{setCurrentResult.msg}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* What the reminder scheduler is about to do */}
      <div style={s.section}>
        <div style={s.sectionTitle}>🔔 PICK REMINDERS</div>
        <div style={s.sectionDesc}>
          Each reminder fires once per week, in the 90 minutes before its
          kickoff, to anyone whose sheet is still short of a pick they could
          still make. Once a window is spent it will not fire again — which used
          to be invisible, and looks identical to it working.
        </div>

        {!pushStatus ? (
          <div style={{ fontSize: 13, color: '#556' }}>Loading…</div>
        ) : !pushStatus.configured ? (
          // "Never set up" and "set up wrong" send you looking in different
          // places, so they don't get the same message.
          <div style={{ ...s.result, ...s.resultError }}>
            {pushStatus.error
              ? <>Push is switched off. The VAPID settings were rejected: <strong>{pushStatus.error}</strong>.
                  {' '}The app is running normally; only notifications are off.</>
              : <>Push is switched off. Missing {(pushStatus.missing || []).join(', ')}.</>}
          </div>
        ) : !pushStatus.reminders ? (
          <div style={{ fontSize: 13, color: '#556' }}>No active week.</div>
        ) : (
          <>
            <div style={s.weekItem}>
              <div>
                <div style={s.weekName}>Would be notified right now</div>
                <div style={s.weekMeta}>
                  Members with at least one unfinished sheet, out of {pushStatus.subscribers} subscribed.
                  {pushStatus.reminders.youWouldBeNotified
                    ? ' You are one of them.'
                    : ' You are not one of them.'}
                </div>
              </div>
              <span style={{
                fontFamily: "'Oswald', sans-serif", fontSize: 22, fontWeight: 700,
                color: pushStatus.reminders.wouldNotifyNow ? '#4caf50' : '#445',
                flexShrink: 0, marginLeft: 12
              }}>{pushStatus.reminders.wouldNotifyNow}</span>
            </div>

            {pushStatus.reminders.points.length === 0 && (
              <div style={{ ...s.weekMeta, marginTop: 10 }}>
                No reminder points this week. The week has no kickoff time set.
              </div>
            )}

            {pushStatus.reminders.points.map(p => {
              const look = {
                sent:      { color: '#4caf50', label: '✓ SENT' },
                in_window: { color: '#ffa726', label: '● WINDOW OPEN' },
                waiting:   { color: '#7fb3ff', label: 'ARMED' },
                missed:    { color: '#ff8888', label: '⚠ MISSED' }
              }[p.state];
              return (
                <div key={p.flag} style={s.weekItem}>
                  <div style={{ minWidth: 0 }}>
                    <div style={s.weekName}>{p.title}</div>
                    <div style={s.weekMeta}>
                      Kickoff {new Date(p.kickoff).toLocaleString()} · window opens{' '}
                      {new Date(p.opensAt).toLocaleTimeString()}
                    </div>
                    <div style={{ ...s.weekMeta, color: '#445' }}>{p.flag}</div>
                  </div>
                  <span style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
                    color: look.color, flexShrink: 0, marginLeft: 12, textAlign: 'right'
                  }}>{look.label}</span>
                </div>
              );
            })}

            {pushStatus.reminders.picksLocked && (
              <div style={{ ...s.weekMeta, marginTop: 12, color: '#ff8888' }}>
                Picks are locked for this week, so no reminder will fire.
              </div>
            )}
          </>
        )}
      </div>

      {/* The funnel that decides the native-app question */}
      <div style={s.section}>
        <div style={s.sectionTitle}>📈 INSTALL &amp; NOTIFICATION FUNNEL</div>
        <div style={s.sectionDesc}>
          People, not visits. Each row counts distinct members over the last 90
          days. Push spent months live with nobody subscribed and no way to see
          it; this is so the next gap shows itself.
        </div>
        {!funnel ? (
          <div style={{ fontSize: 13, color: '#556' }}>No funnel data yet.</div>
        ) : (
          <>
            {[
              ['Opened the app', funnel.active, 'Anyone who launched it at all, the denominator.'],
              ['Installed to home screen', funnel.installed, 'Launched from the installed app, not a tab.'],
              ['Saw the notification ask', funnel.promptSeen, 'The prompt was genuinely on screen.'],
              ['Turned notifications on', funnel.enabled, 'Said yes and the subscription reached us.'],
              ['Said no', funnel.denied, 'Only the device settings can undo this.'],
              ['iPhone, could not be asked', funnel.blockedNeedsInstall, 'Apple allows web push only once installed, the gap a native app closes.'],
              ['Came back from a notification', funnel.openedFromNotification, 'Whether push earns its keep.'],
            ].map(([label, value, note]) => (
              <div key={label} style={s.weekItem}>
                <div style={{ minWidth: 0 }}>
                  <div style={s.weekName}>{label}</div>
                  <div style={s.weekMeta}>{note}</div>
                </div>
                <span style={{
                  fontFamily: "'Oswald', sans-serif", fontSize: 22, fontWeight: 700,
                  color: value ? '#7fb3ff' : '#445', flexShrink: 0, marginLeft: 12
                }}>{value ?? 0}</span>
              </div>
            ))}
            <div style={{ ...s.weekMeta, marginTop: 12 }}>
              Subscribed right now: <strong style={{ color: '#99a' }}>{funnelSubs ?? 0}</strong>
              {' '}Separate from "turned it on", because dead subscriptions get pruned.
            </div>
          </>
        )}
      </div>

      {/* Which build is this? */}
      <div style={s.section}>
        <div style={s.sectionTitle}>⚙️ BUILD</div>
        <div style={s.sectionDesc}>
          What this device is running, and what the server is serving. If they
          differ, this device is on an old copy. Reload it.
        </div>
        <div style={s.weekItem}>
          <div>
            <div style={s.weekName}>This device</div>
            <div style={s.weekMeta}>{shortHash(version.mine) || 'dev build (unhashed)'}</div>
          </div>
          {/* Only claim "current" when the two are actually comparable — saying
              so on a dev build, or when the server didn't answer, would be the
              same guesswork this section exists to end. */}
          {version.stale ? (
            <button style={{ ...s.btn, padding: '6px 14px', marginBottom: 0 }}
                    onClick={() => window.location.reload()}>Reload</button>
          ) : version.mine && version.serverBundle ? (
            <span style={s.activeBadge}>✓ CURRENT</span>
          ) : (
            <span style={{ fontSize: 12, color: '#667' }}>can't compare</span>
          )}
        </div>
        <div style={s.weekItem}>
          <div>
            <div style={s.weekName}>Server</div>
            <div style={s.weekMeta}>
              {shortHash(version.serverBundle) || 'no build on disk'}
              {version.startedAt && ', up since ' + new Date(version.startedAt).toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      </div>{/* grid */}

      <div style={{ height: 40 }} />

      {/* The chat itself. Same component the profile card opens, so a thread
          started from this page is the thread they already have rather than a
          second inbox that only the owner can see. */}
      {dmUserId && (
        <UserStatsModal userId={dmUserId} me={user} onClose={() => setDmUserId(null)} />
      )}
    </div>
  );
}
